import { QrCode, WalletCards } from "lucide-react";
import type {
  CurrencyCode,
  PaymentQrAssetMap,
  PaymentQrMethod,
  PaymentQrSettings,
} from "@/domain/types";

interface Props {
  value: PaymentQrSettings;
  currency: CurrencyCode;
  assets: PaymentQrAssetMap;
  disabled?: boolean;
  onChange: (value: PaymentQrSettings) => void;
}

function nextPreferred(
  current: PaymentQrSettings,
  method: PaymentQrMethod,
  enabled: boolean,
): PaymentQrMethod {
  if (enabled) {
    return current.gcashEnabled || current.maribankEnabled
      ? current.preferred
      : method;
  }
  if (current.preferred !== method) return current.preferred;
  if (method === "gcash" && current.maribankEnabled) return "maribank";
  if (method === "maribank" && current.gcashEnabled) return "gcash";
  return "gcash";
}

export function PaymentQrOptionsField({
  value,
  currency,
  assets,
  disabled = false,
  onChange,
}: Props) {
  const methods: PaymentQrMethod[] = ["gcash", "maribank"];

  const updateEnabled = (method: PaymentQrMethod, enabled: boolean) => {
    const next: PaymentQrSettings = {
      ...value,
      gcashEnabled: method === "gcash" ? enabled : value.gcashEnabled,
      maribankEnabled: method === "maribank" ? enabled : value.maribankEnabled,
      preferred: nextPreferred(value, method, enabled),
    };
    onChange(next);
  };

  return (
    <div className="sm:col-span-2">
      <div className="rounded-2xl border border-border bg-surface-muted/45 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-brand-700 shadow-sm">
            <WalletCards className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-black text-ink">Payment QR options</p>
            <p className="mt-1 text-xs leading-5 text-ink-muted">
              Show this workspace&apos;s approved GCash or MariBank QR on the secure client page. The preferred method is also added to the invoice PDF.
            </p>
          </div>
        </div>

        {currency !== "PHP" ? (
          <div className="mt-4 rounded-xl border border-gold-100 bg-gold-50 px-4 py-3 text-xs leading-5 text-gold-800">
            This invoice is billed in {currency}, while these QR methods receive PHP. Confirm the converted PHP amount with the client and include it in the client-facing note or payment instructions before sending the invoice.
          </div>
        ) : null}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {methods.map((method) => {
            const asset = assets[method];
            const enabled = method === "gcash" ? value.gcashEnabled : value.maribankEnabled;
            const preferred = enabled && value.preferred === method;
            const unavailable = !asset.hasImage;

            return (
              <div
                key={method}
                className={`rounded-xl border p-3 transition ${
                  enabled ? "border-brand-200 bg-white" : "border-border bg-white/60"
                }`}
              >
                <label className={`flex items-start gap-3 ${unavailable ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-border accent-brand-600"
                    checked={enabled && !unavailable}
                    disabled={disabled || unavailable}
                    onChange={(event) => updateEnabled(method, event.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-bold text-ink">
                      <QrCode className="h-4 w-4 text-ink-muted" />
                      {asset.label}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-ink-muted">
                      {asset.hasImage
                        ? asset.accountLabel || "QR configured in workspace settings"
                        : "No QR uploaded for this workspace yet"}
                    </span>
                  </span>
                </label>

                {enabled && !unavailable ? (
                  <label className="mt-3 flex cursor-pointer items-center gap-2 border-t border-border pt-3 text-xs font-semibold text-ink-muted">
                    <input
                      type="radio"
                      name="preferred-payment-qr"
                      className="h-4 w-4 accent-brand-600"
                      checked={preferred}
                      disabled={disabled}
                      onChange={() => onChange({ ...value, preferred: method })}
                    />
                    Preferred in PDF
                  </label>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
